import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES } from 'utils/constants';
import { Schedule } from 'types/schedule';
import Button from 'components/Button';
import Toast from 'components/Toast';
import RefreshButton from 'components/RefreshButton';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import styles from './Schedules.module.scss';

const emptyForm = {
  name: '',
  description: '',
  cronExpression: '0 9 * * 1',
  timezone: 'UTC',
  targetUrl: '',
  httpMethod: 'POST' as 'GET' | 'POST',
  requestBodyJson: '',
  enabled: true,
};

function Schedules() {
  const {
    get,
    post,
    patch,
    delete: deleteSchedule,
    companyId,
    isCompanyInitialized,
  } = useApiClient();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Schedule | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Schedule | null>(null);

  const load = useCallback(async () => {
    if (!isCompanyInitialized || !companyId) {
      return;
    }
    setReloading(true);
    try {
      const data = await get<Schedule[]>(API_ROUTES.SCHEDULES);
      setSchedules(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load schedules';
      setToast({ message: msg, type: 'error' });
    } finally {
      setLoading(false);
      setReloading(false);
    }
  }, [get, isCompanyInitialized, companyId]);

  useEffect(() => {
    if (isCompanyInitialized && companyId) {
      void load();
    }
  }, [isCompanyInitialized, companyId, load]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return schedules;
    return schedules.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.cronExpression.toLowerCase().includes(q) ||
        s.targetUrl.toLowerCase().includes(q),
    );
  }, [schedules, searchQuery]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (s: Schedule) => {
    setEditing(s);
    setForm({
      name: s.name,
      description: s.description ?? '',
      cronExpression: s.cronExpression,
      timezone: s.timezone || 'UTC',
      targetUrl: s.targetUrl,
      httpMethod: s.httpMethod,
      requestBodyJson:
        s.requestBody && Object.keys(s.requestBody).length > 0
          ? JSON.stringify(s.requestBody, null, 2)
          : '',
      enabled: s.enabled,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let requestBody: Record<string, unknown> | undefined;
    if (form.requestBodyJson.trim()) {
      try {
        requestBody = JSON.parse(form.requestBodyJson) as Record<
          string,
          unknown
        >;
      } catch {
        setToast({ message: 'Request body must be valid JSON', type: 'error' });
        return;
      }
    }

    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        cronExpression: form.cronExpression.trim(),
        timezone: form.timezone.trim() || 'UTC',
        targetUrl: form.targetUrl.trim(),
        httpMethod: form.httpMethod,
        enabled: form.enabled,
      };
      if (requestBody) {
        payload.requestBody = requestBody;
      }

      if (editing) {
        const updated = await patch<Schedule>(
          `${API_ROUTES.SCHEDULES}/${editing.id}`,
          payload,
        );
        setSchedules((prev) =>
          prev.map((x) => (x.id === updated.id ? updated : x)),
        );
        setToast({ message: 'Schedule updated', type: 'success' });
      } else {
        const created = await post<Schedule>(API_ROUTES.SCHEDULES, payload);
        setSchedules((prev) =>
          [...prev, created].sort((a, b) => a.name.localeCompare(b.name)),
        );
        setToast({ message: 'Schedule created', type: 'success' });
      }
      setShowModal(false);
      setEditing(null);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Could not save schedule';
      setToast({ message: msg, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      await deleteSchedule(`${API_ROUTES.SCHEDULES}/${deleteTarget.id}`);
      setSchedules((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      setToast({ message: 'Schedule deleted', type: 'success' });
      setDeleteTarget(null);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Could not delete schedule';
      setToast({ message: msg, type: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  if (!isCompanyInitialized) {
    return (
      <div className={styles.container}>
        <p className={styles.empty}>Loading company…</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => {
            setToast(null);
          }}
        />
      )}

      <div className={styles.header}>
        <h1 className={styles.title}>Schedules</h1>
        <div className={styles.headerButtons}>
          <RefreshButton
            onClick={() => {
              void load();
            }}
            loading={reloading}
          />
          <Button type="button" onClick={openCreate}>
            <Plus size={18} style={{ marginRight: '0.5rem' }} />
            New schedule
          </Button>
        </div>
      </div>

      <p className={styles.helpText}>
        Define cron jobs for your active company. The Abrasoft scheduler service
        calls your target URL when each schedule fires; last run times are
        stored in the hub.
      </p>

      <div style={{ marginBottom: '1rem', maxWidth: '24rem' }}>
        <div style={{ position: 'relative' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'hsl(0 0% 50%)',
              pointerEvents: 'none',
            }}
          />
          <input
            type="search"
            placeholder="Search schedules…"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 2.5rem',
              borderRadius: 8,
              border: '1.5px solid hsl(0 0% 89%)',
              fontFamily: 'inherit',
            }}
          />
        </div>
      </div>

      {loading ? (
        <p className={styles.empty}>Loading schedules…</p>
      ) : filtered.length === 0 ? (
        <p className={styles.empty}>
          {schedules.length === 0
            ? 'No schedules yet. Create one to collaborate with automated webhooks.'
            : 'No schedules match your search.'}
        </p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Cron</th>
                <th>Target</th>
                <th>Status</th>
                <th>Last run</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id}>
                  <td>
                    <strong>{s.name}</strong>
                    {s.description ? (
                      <div style={{ fontSize: '0.8125rem', color: '#666' }}>
                        {s.description}
                      </div>
                    ) : null}
                  </td>
                  <td className={styles.mono}>
                    {s.cronExpression}
                    <div style={{ color: '#888' }}>{s.timezone}</div>
                  </td>
                  <td className={styles.mono}>
                    {s.httpMethod} {s.targetUrl}
                  </td>
                  <td>
                    <span
                      className={`${styles.badge} ${s.enabled ? styles.badgeOn : styles.badgeOff}`}
                    >
                      {s.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className={styles.mono}>
                    {s.lastRunAt ? new Date(s.lastRunAt).toLocaleString() : '—'}
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          openEdit(s);
                        }}
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setDeleteTarget(s);
                        }}
                        disabled={deletingId === s.id}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editing ? 'Edit schedule' : 'New schedule'}
          </Modal.Title>
        </Modal.Header>
        <Form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
        >
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                value={form.name}
                onChange={(e) => {
                  setForm((f) => ({ ...f, name: e.target.value }));
                }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                onChange={(e) => {
                  setForm((f) => ({ ...f, description: e.target.value }));
                }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Cron expression</Form.Label>
              <Form.Control
                required
                className={styles.mono}
                value={form.cronExpression}
                onChange={(e) => {
                  setForm((f) => ({ ...f, cronExpression: e.target.value }));
                }}
                placeholder="0 9 * * 1"
              />
              <Form.Text muted>
                Standard five-field cron (minute hour day month weekday).
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Timezone</Form.Label>
              <Form.Control
                value={form.timezone}
                onChange={(e) => {
                  setForm((f) => ({ ...f, timezone: e.target.value }));
                }}
                placeholder="UTC"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Target URL</Form.Label>
              <Form.Control
                required
                value={form.targetUrl}
                onChange={(e) => {
                  setForm((f) => ({ ...f, targetUrl: e.target.value }));
                }}
                placeholder="https://example.com/webhook"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>HTTP method</Form.Label>
              <Form.Select
                value={form.httpMethod}
                onChange={(e) => {
                  setForm((f) => ({
                    ...f,
                    httpMethod: e.target.value as 'GET' | 'POST',
                  }));
                }}
              >
                <option value="POST">POST</option>
                <option value="GET">GET</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>JSON body (optional, POST only)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                className={styles.mono}
                value={form.requestBodyJson}
                onChange={(e) => {
                  setForm((f) => ({ ...f, requestBodyJson: e.target.value }));
                }}
                placeholder='{"companyId":"…"}'
              />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Check
                type="checkbox"
                label="Enabled"
                checked={form.enabled}
                onChange={(e) => {
                  setForm((f) => ({ ...f, enabled: e.target.checked }));
                }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete schedule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void confirmDelete()}
            disabled={!!deletingId}
          >
            {deletingId ? 'Deleting…' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Schedules;
